import { ContactIntegrationStatus } from "@/components/integrations/employee/employee-integrations-sync";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useTranslation } from "@/hooks/useTranslation";
import { ContactCrmIntegration } from "@shaarp/common";
import { ExternalLink, RefreshCwIcon, SearchIcon } from "lucide-react";

type SearchResultsProps = {
  integration: ContactIntegrationStatus;
  possiblesMatchCrmContacts: ContactCrmIntegration[];
  onSyncToContact: (externalContactId: string) => void;
  onOpenExternal: (crmContact: ContactCrmIntegration) => void;
};

export const SearchResults = ({
  integration,
  possiblesMatchCrmContacts,
  onSyncToContact,
  onOpenExternal,
}: SearchResultsProps) => {
  const { t } = useTranslation();

  if (possiblesMatchCrmContacts.length === 0) {
    return null;
  }

  return (
    <>
      <div className="px-3 py-2 border-t border-border/50">
        <div className="flex gap-3 items-center text-sm font-medium text-muted-foreground">
          <SearchIcon className="w-4 h-4" />
          {t("employee.integrations.searchResults")}
        </div>
      </div>

      {possiblesMatchCrmContacts.map((crmContact) => (
        <DropdownMenuItem
          key={crmContact.id}
          className="flex gap-4 items-center px-3 py-3"
          disabled
        >
          <img
            src={integration.logo}
            alt={integration.name}
            className="object-contain shrink-0 w-6 h-6"
          />
          <div className="flex flex-col flex-1 gap-1">
            <span className="text-sm font-medium">
              {crmContact.firstName && crmContact.lastName
                ? `${crmContact.firstName} ${crmContact.lastName}`
                : crmContact.email || "Unknown Contact"}
            </span>
            <span className="text-xs text-muted-foreground">
              {crmContact.email}
            </span>
          </div>
          <div className="flex shrink-0 gap-2 items-center">
            <Button
              variant="ghost"
              size="sm"
              className="p-0 w-8 h-8 hover:bg-muted"
              disabled={!crmContact.directLink}
              onClick={(e) => {
                e.stopPropagation();
                onOpenExternal(crmContact);
              }}
              title={t("employee.integrations.openInIntegration", {
                integration: integration.name,
              })}
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="p-0 w-8 h-8 hover:bg-muted"
              disabled={integration.isLoading}
              onClick={(e) => {
                e.stopPropagation();
                onSyncToContact(crmContact.id);
              }}
              title={t("employee.integrations.syncToIntegration", {
                integration: integration.name,
              })}
            >
              {/* Only the candidate being linked spins: the others are
                  merely held back until that send settles. */}
              <RefreshCwIcon
                className={`w-4 h-4 ${
                  integration.loadingMatchId === crmContact.id
                    ? "animate-spin"
                    : ""
                }`}
              />
            </Button>
          </div>
        </DropdownMenuItem>
      ))}
    </>
  );
};
